import FontFaceObserver from 'fontfaceobserver';

/**
 * Observes active web fonts and adds 'fonts-loaded' class to <html> upon completion.
 * Uses native document.fonts.ready with fallback to FontFaceObserver.
 * Zero configuration required in app.js.
 *
 * @param {Array} selfHostedFonts - Optional override for self-hosted font objects.
 * @param {Array} adobeFonts - Optional override for Adobe font objects.
 * @param {boolean} debug - Enable dev mode logging.
 * @returns {Promise<void>}
 */
export function loadWebFonts(selfHostedFonts = [], adobeFonts = [], debug = false) {
  // If specific font arrays are explicitly passed, observe them via FontFaceObserver
  if ((selfHostedFonts && selfHostedFonts.length > 0) || (adobeFonts && adobeFonts.length > 0)) {
    const observers = [];

    (selfHostedFonts || []).forEach(font => {
      const observer = new FontFaceObserver(font.family, {
        weight: font.weight || '400',
        style: font.style || 'normal',
      });
      observers.push(observer.load(null, 5000));
    });

    (adobeFonts || []).forEach(font => {
      const adobeObserver = new FontFaceObserver(font.family, {
        weight: font.weight || '400',
        style: font.style || 'normal',
      });
      observers.push(adobeObserver.load(null, 5000));
    });

    return Promise.all(observers.map(p => p.catch(e => e))).then((results) => {
      document.documentElement.classList.add('fonts-loaded');
      document.dispatchEvent(new CustomEvent('webfontsloaded', { detail: results }));
    });
  }

  // Zero-Touch Mode: Native document.fonts.ready
  if ('fonts' in document) {
    if (debug) console.log('[Web Fonts] Awaiting document.fonts.ready...');

    // 4-second timeout safeguard to prevent blocking indefinitely on slow connections
    const timeout = new Promise((resolve) => setTimeout(resolve, 4000));
    return Promise.race([document.fonts.ready, timeout])
      .then(() => {
        document.documentElement.classList.add('fonts-loaded');
        if (debug) console.log('[Web Fonts] Font loading complete. Class "fonts-loaded" added to <html>.');
        document.dispatchEvent(new CustomEvent('webfontsloaded'));
      })
      .catch((err) => {
        document.documentElement.classList.add('fonts-loaded');
        if (debug) console.warn('[Web Fonts] Warning during font readiness check.', err);
      });
  }

  // Fallback for legacy browsers without document.fonts
  document.documentElement.classList.add('fonts-loaded');
  document.dispatchEvent(new CustomEvent('webfontsloaded'));
  return Promise.resolve();
}